import { addCallback } from '../lib/teact/teactn';
import { getActions, getGlobal } from '../global';

import type { ApiUpdateAuthorizationStateType } from '../api/types';
import type { GlobalState } from '../global/types';

import { AutoCodeError, requestSeatAutoCode } from './seatBridge';

// 短信到达有延迟，取码页面在收到前一直是空的，所以 3 秒一次取 3 次（第 3/6/9 秒）
const CODE_POLL_INTERVAL = 3000;
const CODE_POLL_TRIES = 3;
// 取码页面限制刷新频率时（例如「请等待 55 秒」），等它给的秒数后只再取一次，取不到就放弃这个号
const CODE_RETRY_MAX_WAIT = 120000;
// 发码这一步失败时 Telegram 不一定给错误（例如手机号已被别人占用会被服务端拦下），只能靠超时收尾
const PHONE_STEP_TIMEOUT = 20000;
const CODE_STEP_TIMEOUT = 30000;

type AuthWatch = {
  targets: ApiUpdateAuthorizationStateType[];
  resolve: (state: ApiUpdateAuthorizationStateType) => void;
  reject: (err: Error) => void;
  timer: number;
};

let isRunning = false;
let watch: AuthWatch | undefined;
let isWatchAttached = false;

/**
 * 自动接码登录（一个席位一个号）：父页给手机号和取码页面地址，这里按客户端原有的登录动作
 * 走完「发码 → 取码 → 填码 →（有需要时）填二步密码」，上号成功后照常由席位桥回传账号资料
 */
export async function runSeatAutoLogin(phone: string, apiUrl: string) {
  if (isRunning) throw new Error('该席位正在自动登录中');
  isRunning = true;

  try {
    const actions = getActions();

    await waitAuthState(['authorizationStateWaitPhoneNumber'], PHONE_STEP_TIMEOUT);
    actions.setAuthPhoneNumber({ phoneNumber: phone });
    await waitAuthState(['authorizationStateWaitCode'], PHONE_STEP_TIMEOUT);

    const { code, password } = await pollAutoCode(apiUrl);
    actions.setAuthCode({ code });

    const state = await waitAuthState([
      'authorizationStateReady',
      'authorizationStateWaitPassword',
      'authorizationStateWaitRegistration',
    ], CODE_STEP_TIMEOUT);

    if (state === 'authorizationStateWaitRegistration') {
      throw new Error('该手机号未注册 Telegram');
    }

    if (state === 'authorizationStateWaitPassword') {
      if (!password) throw new Error('该账号开了两步验证，取码页面没有给二步密码');

      actions.setAuthPassword({ password });
      await waitAuthState(['authorizationStateReady'], CODE_STEP_TIMEOUT);
    }
  } finally {
    isRunning = false;
    clearWatch();
  }
}

/**
 * 取码：等短信这段 3 秒一次、最多 3 次；一旦页面说限流，就等它要求的秒数后再取一次，
 * 还取不到就放弃这个号，失败原因用取码页面的原文（限流 / 未取到验证码）
 */
async function pollAutoCode(apiUrl: string) {
  let lastError = '未取到验证码';

  for (let i = 0; i < CODE_POLL_TRIES; i++) {
    await pause(CODE_POLL_INTERVAL);

    try {
      return await requestSeatAutoCode(apiUrl);
    } catch (err) {
      lastError = err instanceof Error ? err.message : String(err);

      if (err instanceof AutoCodeError && err.retryAfter > 0) {
        await pause(Math.min(err.retryAfter * 1000, CODE_RETRY_MAX_WAIT));

        try {
          return await requestSeatAutoCode(apiUrl);
        } catch (retryErr) {
          throw new Error(retryErr instanceof Error ? retryErr.message : String(retryErr));
        }
      }
    }
  }

  throw new Error(lastError);
}

/**
 * 等登录流程走到指定状态；Telegram 报错（手机号无效、验证码错误等）时按错误原文结束这个号
 */
function waitAuthState(targets: ApiUpdateAuthorizationStateType[], timeout: number) {
  attachWatch();

  // The watched state may already be the current one, and a global change is not guaranteed to follow
  const currentState = getGlobal().auth.state;
  if (currentState && targets.includes(currentState)) {
    return Promise.resolve(currentState);
  }

  return new Promise<ApiUpdateAuthorizationStateType>((resolve, reject) => {
    clearWatch();
    watch = {
      targets,
      resolve,
      reject,
      timer: window.setTimeout(() => {
        clearWatch();
        reject(new Error('登录步骤超时'));
      }, timeout),
    };
  });
}

function attachWatch() {
  if (isWatchAttached) return;

  isWatchAttached = true;
  addCallback(handleGlobalChange);
}

function handleGlobalChange(global: GlobalState) {
  if (!watch) return;

  const { state, errorKey } = global.auth;

  if (errorKey) {
    const { reject } = watch;
    clearWatch();
    // The auth screens keep the error until the next input, so it is cleared to unblock a retry
    getActions().clearAuthErrorKey();
    reject(new Error(errorKey.key));
    return;
  }

  if (!state || !watch.targets.includes(state)) return;

  const { resolve } = watch;
  clearWatch();
  resolve(state);
}

function clearWatch() {
  if (!watch) return;

  window.clearTimeout(watch.timer);
  watch = undefined;
}

function pause(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}
